import { reactive } from "vue";
import { defineStore } from "pinia";
import Axios from "axios";
import { debounce } from "lodash-es";
import { getBaseEndpoint, baseApiEndpoint, DEBOUNCE_TIMER } from "@/config/config";



type HouseType = {
  [key: string]: {
    value: boolean;
    id: string;
  };
};


let BASE_API = getBaseEndpoint(process.env.NODE_ENV)

export const useOnboardingStore = defineStore(
  "onboarding",
  () => {
    const PRICE_STORE = usePriceStore();

    // state properties
    const onboardingObject: any = reactive({
      dropdownOpen: false,
      loading: false,
      stepperCount: 0,
      step: "",
      userId: 0,
      title: "",
      description: "",
      size: null,
      bathrooms: 0,
      bedrooms: 0,
      sleeps: 0,
      sleepsClicked: false,
      pets: false,
      facilities: {
        pool: null,
        outdoorPool: null,
        spa: null,
        outdoorSpa: null,
        sauna: null,
        wifi: null,
        washingMachine: null,
      },
      address: {
        fullStreetName: "",
        bbrNumber: "",
        bfeNumber: "",
        streetName: "",
        streetNumber: "",
        city: "",
        zipCode: "",
        latitude: 0,
        longtitude: 0,
        country: {
          id: 0,
          name: "",
          code: "",
        },
      },
      imageData: new FormData(),
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      affiliate: "",
      distanceToWater: {
        text: "Vælg afstand til vand",
        value: 0,
      },
      selfCleaning: null,
      selfCleaningPrice: null,
      intelligentPricing: true,
      cleaningSelections: "",
      servicePartnerId: 0,
      cleaningEnum: 0,
      houseType: {
        holidayHome: {
          type: "Sommerhus",
          id: 212,
          value: false,
        },
        holidayAppartment: {
          type: "Ferielejlighed",
          id: 168,
          value: false,
        },
        bedAndBreakfast: {
          type: "Bed & breakfast",
          id: 155,
          value: false,
        },
        bungalow: {
          type: "Bungalow",
          id: 902,
          value: false,
        },
        townHouse: {
          type: "Byhus",
          id: 186,
          value: false,
        },
        farmHouse: {
          type: "Gårdhus",
          id: 160,
          value: false,
        },
        manorHouse: {
          type: "Herregård",
          id: 529,
          value: false,
        },
        cave: {
          type: "Hule",
          id: 901,
          value: false,
        },
        houseBoat: {
          type: "Husbåd",
          id: 201,
          value: false,
        },
        cottage: {
          type: "Hytte",
          id: 158,
          value: false,
        },
        villageHouse: {
          type: "Landsbyhus",
          id: 187,
          value: false,
        },
        mobileHome: {
          type: "Mobile home",
          id: 900,
          value: false,
        },
        penthouse: {
          type: "Penthouse",
          id: 899,
          value: false,
        },
        terracedHouse: {
          type: "Rækkehus",
          id: 214,
          value: false,
        },
        castle: {
          type: "Slot",
          id: 803,
          value: false,
        },
        villa: {
          type: "Villa",
          id: 199,
          value: false,
        },
        yacht: {
          type: "Yacht",
          id: 426,
          value: false,
        },
      },
      propertyType: 0,
      suggestedHighSeasonPrice: 15555,
      suggestedLowSeasonPrice: 3555,
      estimatedYearlyIncome: undefined,
      rentalToday: false,
    });

    // actions
    /**
     * Method for increasing the value of a property
     * @param property { string } Takes in a key - i.e. 'bedrooms', 'bathrooms', 'sleeps'
     */
    const incrementProperty = (property: string, stepCount: number) => {
      onboardingObject[property]++;

      PRICE_STORE.updatePrices(stepCount);
    };

    /**
     * Method for decreasing the value of a property
     * @param property { string } Takes in a key - i.e. 'bedrooms', 'bathrooms', 'sleeps'
     */
    const decreaseProperty = (property: string, stepCount: number) => {
      if (onboardingObject[property] <= 1) return;
      onboardingObject[property]--;

      PRICE_STORE.updatePrices(stepCount);
    };

    const setPropertyTrue = (property: keyof HouseType) => {
      Object.keys(onboardingObject.houseType).forEach((key) => {
        if (key !== property) {
          onboardingObject.houseType[key as keyof HouseType].value = false;
        }
      });
      onboardingObject.houseType[property].value = true;
      onboardingObject.propertyType = onboardingObject.houseType[property].id;
    };

    return {
      onboardingObject,
      setPropertyTrue,
      incrementProperty,
      decreaseProperty,
    };
  },
  { persist: true }
);

// TODO: Add logic that doesn't send the information that has yet to be inputted i.e. lat/long, distanceToWater etc
export const usePriceStore = defineStore(
  "price",
  () => {
    // state properties
    // if we're on localhost, the endpoint doesn't exist, so use dev01 instead
    if (BASE_API === baseApiEndpoint.development) {
      BASE_API = "https://dev01.campaya.dk"
    }

    const priceObject = reactive({
      priceRange: [0, 800000], //default values
      suggestedHighSeasonPrice: 15555,
      suggestedLowSeasonPrice: 3555,
      estimatedYearlyIncome: undefined,
    });

    const updatePrices = debounce(async (stepCount: number) => {
      const ONBOARDING_OBJECT = useOnboardingStore().$state.onboardingObject;
      let _mock;

      ONBOARDING_OBJECT.loading = true

      if (stepCount == 0) {
        _mock = await sendPriceUpdateRequest({
          bedrooms: ONBOARDING_OBJECT.bedrooms,
          sleeps: ONBOARDING_OBJECT.sleeps,
        })
      }


      if (stepCount == 1) {
        _mock = await sendPriceUpdateRequest({
          bedrooms: ONBOARDING_OBJECT.bedrooms,
          sleeps: ONBOARDING_OBJECT.sleeps,
          distanceToWater: ONBOARDING_OBJECT.distanceToWater.value,
        })
      }


      if (stepCount == 2) {
        _mock = await sendPriceUpdateRequest({
          bedrooms: ONBOARDING_OBJECT.bedrooms,
          sleeps: ONBOARDING_OBJECT.sleeps,
          distanceToWater: ONBOARDING_OBJECT.distanceToWater.value,
          bathrooms: ONBOARDING_OBJECT.bathrooms,
        })
      }


      if (stepCount == 3) {
        _mock = await sendPriceUpdateRequest({
          bedrooms: ONBOARDING_OBJECT.bedrooms,
          sleeps: ONBOARDING_OBJECT.sleeps,
          distanceToWater: ONBOARDING_OBJECT.distanceToWater.value,
          bathrooms: ONBOARDING_OBJECT.bathrooms,
          size: ONBOARDING_OBJECT.size
        })
      }


      if (stepCount == 4) {
        _mock = await sendPriceUpdateRequest({
          bedrooms: ONBOARDING_OBJECT.bedrooms,
          sleeps: ONBOARDING_OBJECT.sleeps,
          distanceToWater: ONBOARDING_OBJECT.distanceToWater.value,
          bathrooms: ONBOARDING_OBJECT.bathrooms,
          size: ONBOARDING_OBJECT.size,
          facilitiesIndoorPool: ONBOARDING_OBJECT.facilities.pool,
          facilitiesOutdoorPool: ONBOARDING_OBJECT.facilities.outdoorPool
        })
      }


      if (stepCount == 5) {
        // set the values from before so that they have false if it weren't selected
        ONBOARDING_OBJECT.facilities.pool = ONBOARDING_OBJECT.facilities.pool ? true : false
        ONBOARDING_OBJECT.facilities.outdoorPool = ONBOARDING_OBJECT.facilities.outdoorPool ? true : false


        _mock = await sendPriceUpdateRequest({
          bedrooms: ONBOARDING_OBJECT.bedrooms,
          sleeps: ONBOARDING_OBJECT.sleeps,
          distanceToWater: ONBOARDING_OBJECT.distanceToWater.value,
          bathrooms: ONBOARDING_OBJECT.bathrooms,
          size: ONBOARDING_OBJECT.size,
          facilitiesIndoorPool: ONBOARDING_OBJECT.facilities.pool,
          facilitiesOutdoorPool: ONBOARDING_OBJECT.facilities.outdoorPool,
          facilitiesJacuzzi: ONBOARDING_OBJECT.facilities.spa,
          facilitiesSauna: ONBOARDING_OBJECT.facilities.sauna,
          facilitiesOutdoorSpa: ONBOARDING_OBJECT.facilities.outdoorSpa,
        })
      }


      if (stepCount == 6) {
        // set the values from before so that they have false if it weren't selected
        ONBOARDING_OBJECT.facilities.spa = ONBOARDING_OBJECT.facilities.spa ? true : false
        ONBOARDING_OBJECT.facilities.sauna = ONBOARDING_OBJECT.facilities.sauna ? true : false
        ONBOARDING_OBJECT.facilities.outdoorSpa = ONBOARDING_OBJECT.facilities.outdoorSpa ? true : false

        _mock = await sendPriceUpdateRequest({
          bedrooms: ONBOARDING_OBJECT.bedrooms ?? null,
          sleeps: ONBOARDING_OBJECT.sleeps ?? null,
          distanceToWater: ONBOARDING_OBJECT.distanceToWater.value ?? null,
          bathrooms: ONBOARDING_OBJECT.bathrooms ?? null,
          size: ONBOARDING_OBJECT.size ?? null,
          facilitiesIndoorPool: ONBOARDING_OBJECT.facilities.pool ?? null,
          facilitiesOutdoorPool: ONBOARDING_OBJECT.facilities.outdoorPool ?? null,
          facilitiesJacuzzi: ONBOARDING_OBJECT.facilities.spa ?? null,
          facilitiesSauna: ONBOARDING_OBJECT.facilities.sauna ?? null,
          facilitiesOutdoorSpa: ONBOARDING_OBJECT.facilities.outdoorSpa ?? null,
          mapLat: ONBOARDING_OBJECT.address.latitude ?? null,
          mapLon: ONBOARDING_OBJECT.address.longtitude ?? null,
          type: ONBOARDING_OBJECT.propertyType ?? null
        })
      }
      return _mock;
    }, DEBOUNCE_TIMER);




    const sendPriceUpdateRequest = async (data: any) => {
      const ONBOARDING_OBJECT = useOnboardingStore().$state.onboardingObject;

      return await Axios.post(
        `${BASE_API}/api/PriceEstimator/GetEstimatedPrice`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          withCredentials: true,
        }
      )
        .then((res) => {
          if (res.data) {
            priceObject.priceRange = [
              res.data.lowerPrice,
              res.data.upperPrice,
            ];
            priceObject.suggestedHighSeasonPrice = res.data.aHighSeasonPrice;
            priceObject.suggestedLowSeasonPrice = res.data.fLowSeasonPrice;
            priceObject.estimatedYearlyIncome = res.data.estimatedYearlyIncome;

            return {
              lowerPrice: res.data.lowerPrice,
              upperPrice: res.data.upperPrice,
            };
          } else {
            priceObject.priceRange = [0, 550000];
            return { lowerPrice: 0, upperPrice: 550000 };
          }
        })
        .catch((err) => {
          console.log("❌: ", err);
        }).finally(() => {
          ONBOARDING_OBJECT.loading = false
        });
    }

    return { priceObject, updatePrices };
  },
  { persist: true }
);

export const useErrorStore = defineStore(
  "error",
  () => {
    // state properties
    const errorObject = reactive({
      firstName: false,
      lastName: false,
      email: false,
      phoneNumber: false,
    });

    return { errorObject };
  },
  { persist: true }
);
